import { Button, IconButton, Input, Typography, Dialog, Menu } from "@material-tailwind/react";
import { HiOutlinePlus, HiSearch, HiX, HiOutlineChevronLeft, HiOutlineChevronRight, HiOutlineDotsHorizontal, HiOutlineExclamationCircle } from "react-icons/hi";
import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import IngredientAPI from '../api/ingredient';
import { toast } from 'react-toastify';
import debounce from 'lodash/debounce';
import { useDialog } from '../context/DialogContext';
import PaginationCustom from '../components/molecules/PaginationCustom';

const SkeletonRow = () => (
  <div className="w-full h-12 rounded bg-primary/5 animate-pulse"></div>
);

const Ingredient = () => {
  const [ingredients, setIngredients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [pagination, setPagination] = useState({
    page: 1,
    totalItems: 0,
    totalPages: 0,
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ingredientToDelete, setIngredientToDelete] = useState(null);

  // Dialog states
  const { openIngredientDialog } = useDialog();
  const [selectedIngredientUri, setSelectedIngredientUri] = useState(null);

  const currentPage = parseInt(searchParams.get('page') || '1', 10) || 1;
  const currentLimit = parseInt(searchParams.get('limit') || '50', 10) || 50;

  const debouncedSearch = useCallback(
    debounce((keyword, page, limit) => {
      if (keyword) {
        searchIngredients(keyword, page, limit);
      } else {
        fetchIngredients(page, limit);
      }
    }, 500),
    []
  );

  useEffect(() => {
    const page = parseInt(searchParams.get('page') || '1', 10);
    const search = searchParams.get('search') || '';
    const limit = currentLimit;
    setSearchKeyword(search);
    debouncedSearch(search, page, limit);
  }, [searchParams]);

  const handleSearch = (e) => {
    const keyword = e.target.value;
    setSearchKeyword(keyword);
    setSearchParams({ page: '1', search: keyword, limit: currentLimit.toString() });
  };

  const handlePageChange = (newPage) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set('page', newPage.toString());
      params.set('limit', currentLimit.toString());
      return params;
    });
  };

  const handleSizeChange = (newSize) => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);
      params.set('page', '1');
      params.set('limit', newSize.toString());
      return params;
    });
  };

  const searchIngredients = async (keyword, page, limit) => {
    try {
      setLoading(true);
      const response = await IngredientAPI.search(keyword, page, limit);
      setIngredients(response.results);
      setPagination({
        page: response.pagination?.currentPage || 1,
        totalItems: response.pagination?.totalItems || 0,
        totalPages: response.pagination?.totalPages || 0
      });
    } catch (error) {
      toast.error('Failed to search ingredients');
    } finally {
      setLoading(false);
    }
  };

  const fetchIngredients = async (page, limit = 50) => {
    try {
      setLoading(true);
      const response = await IngredientAPI.getByPage(page, limit);
      setIngredients(response.results);
      setPagination({
        page: response.pagination?.currentPage || 1,
        totalItems: response.pagination?.totalItems || 0,
        totalPages: response.pagination?.totalPages || 0
      });
    } catch (error) {
      toast.error('Failed to fetch ingredients');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (ingredient) => {
    setIngredientToDelete(ingredient);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const response = await IngredientAPI.delete(ingredientToDelete._id);
      if (response) {
        toast.success('Ingredient deleted successfully');
        setDeleteDialogOpen(false);
        setIngredientToDelete(null);

        if (searchKeyword) {
          searchIngredients(searchKeyword, currentPage, currentLimit);
        } else {
          fetchIngredients(currentPage, currentLimit);
        }
      }
    } catch (error) {
      toast.error('Failed to delete ingredient');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (ingredientUri) => {
    openIngredientDialog({
      mode: 'edit',
      ingredientUri: ingredientUri,
      onSuccess: (updatedIngredient) => {
        if (searchKeyword) {
          searchIngredients(searchKeyword, currentPage, currentLimit);
        } else {
          fetchIngredients(currentPage, currentLimit);
        }
        toast.success(`Ingredient ${updatedIngredient.name} updated successfully`);
      }
    });
  };

  const handleCreateClick = () => {
    openIngredientDialog({
      mode: 'create',
      onSuccess: (newIngredient) => {
        fetchIngredients(); // Refresh the list after creation
        toast.success(`Ingredient ${newIngredient.name} created successfully`);
      }
    });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="w-full max-w-[300px]">
          <Input
            className="bg-white"
            placeholder="Search by name"
            disabled={loading}
            value={searchKeyword}
            onChange={handleSearch}
          >
            <Input.Icon>
              <HiSearch className="size-full" />
            </Input.Icon>
          </Input>
        </div>
        <Button
          className="flex items-center gap-2 px-4"
          disabled={loading}
          onClick={handleCreateClick}
        >
          <HiOutlinePlus className="w-4 h-4" /> New Ingredient
        </Button>
      </div>

      {!loading && pagination.totalItems > 0 && (
        <PaginationCustom
          currentPage={currentPage}
          pageSize={currentLimit}
          totalItems={pagination.totalItems}
          onPageChange={handlePageChange}
          onSizeChange={handleSizeChange}
          loading={loading}
        />
      )}

      <div className="grid grid-cols-1 gap-2 mb-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        {loading ? (
          Array.from({ length: 20 }).map((_, index) => (
            <SkeletonRow key={index} />
          ))
        ) : ingredients?.map((ingredient) => (
          <div
            key={ingredient._id}
            onClick={() => handleEditClick(ingredient.uri)}
            className="relative h-full px-4 py-3 leading-none transition bg-white border rounded-md shadow-none cursor-pointer text-primary hover:border-primary/50 group"
          >
            <span className="break-words text-14">{ingredient.name}</span>
            <div className="absolute hidden -top-2 -right-2 group-hover:block">
              <IconButton
                size="sm"
                className="min-w-0 min-h-0 duration-150 border rounded-full size-5 border-primary hover:bg-red-500 hover:text-white hover:border-red-500"
                color="secondary"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleDelete(ingredient);
                }}
              >
                <HiX className="size-3" />
              </IconButton>
            </div>
          </div>
        ))}
        {!loading && (ingredients?.length === 0 || !ingredients)  && (
          <div className="flex flex-col items-center justify-center w-full h-full gap-5 col-span-full">
            <HiOutlineExclamationCircle className="opacity-50 size-12 text-blue-gray-400" />
            <Typography className="font-normal text-blue-gray-400">
              {searchKeyword ? 'No ingredients found matching your search' : 'No ingredients found'}
            </Typography>
            {!searchKeyword && (
              <Button
                className="flex items-center gap-2 px-4"
                onClick={handleCreateClick}
              >
                <HiOutlinePlus className="w-4 h-4" /> Create Ingredient
              </Button>
            )}
          </div>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        handler={() => setDeleteDialogOpen(false)}
        size="sm"
      >
        <Dialog.Overlay>
          <Dialog.Content>
            <div className="p-0">
              <div className="p-4 text-center">
                <Typography variant="h5" className="mb-4">
                  Delete Ingredient
                </Typography>
                <Typography>
                  Are you sure you want to delete "{ingredientToDelete?.name}"?<br />
                  This action cannot be undone.
                </Typography>
              </div>
              <div className="flex justify-center gap-4 p-4 border-t">
                <Button
                  onClick={() => setDeleteDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={confirmDelete}
                  disabled={loading}
                  color="warning"
                  variant="outline"
                >
                  {loading ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog>
    </div>
  );
};

export default Ingredient;
